import { createContext, useContext, useReducer, useRef } from 'react';
import useIsomorphicEffect from '../../../utilities/useIsomorphicEffect';

export interface State {
  totalItems: number;
  currentIndex: number;
  prevIndex?: number;
  nextIndex?: number;
  speed: number;
  triggerType?: string;
}

type TriggerType = 'arrow' | 'dot';

export const TRIGGER = {
  ARROW: 'arrow' as TriggerType,
  DOT: 'dot' as TriggerType,
};

export type Type = 'prev' | 'next' | 'current' | 'reset';
type Callback = (state: State, type: Type) => void;

interface Action {
  type: Type;
  data?: number;
  callback?: Callback;
  triggerType?: TriggerType;
}

export const DEFAULT_STATE: State = {
  currentIndex: 0,
  totalItems: 0,
  speed: 500,
};

// null value because it CarouselProvider always provides the default value which is a reducer
const Context = createContext<State>(DEFAULT_STATE);
const DispatchContext = createContext<React.Dispatch<Action>>(() => {});

const getNextState = (
  state: State,
  newCurrentIndex: number,
  newTotalItems?: number,
  triggerType?: TriggerType
): State => ({
  ...state,
  totalItems: newTotalItems ?? state.totalItems,
  prevIndex: newCurrentIndex === 0 ? state.totalItems - 1 : newCurrentIndex - 1,
  currentIndex: newCurrentIndex,
  nextIndex: newCurrentIndex === state.totalItems - 1 ? 0 : newCurrentIndex + 1,
  triggerType,
});

export const reducer = (state: State, action: Action): State => {
  const type = action.type;
  switch (action.type) {
    case 'prev': {
      const newCurrentIndex =
        state.currentIndex < 1 ? state.totalItems - 1 : state.currentIndex - 1;
      const currState = getNextState(
        state,
        newCurrentIndex,
        state.totalItems,
        action.triggerType
      );

      if (action.callback) {
        action.callback(currState, type);
      }
      return currState;
    }
    case 'next': {
      const newCurrentIndex =
        state.currentIndex === state.totalItems - 1
          ? 0
          : state.currentIndex + 1;
      const currState = getNextState(
        state,
        newCurrentIndex,
        state.totalItems,
        action.triggerType
      );

      if (action.callback) {
        action.callback(currState, type);
      }
      return currState;
    }
    case 'current': {
      const newCurrentIndex = action.data;
      const currState = getNextState(
        state,
        newCurrentIndex ?? 0,
        state.totalItems,
        action.triggerType
      );

      if (action.callback) {
        action.callback(currState, type);
      }
      return currState;
    }
    case 'reset': {
      const currState = getNextState(state, 0, action.data, action.triggerType);

      return currState;
    }
  }
};

export const useCarousel = (): [
  State,
  {
    previous: (callback?: Callback, triggerType?: TriggerType) => void;
    next: (callback?: Callback, triggerType?: TriggerType) => void;
    current: (
      indx: number,
      callback?: Callback,
      triggerType?: TriggerType
    ) => void;
  },
] => {
  const state = useContext(Context);
  const dispatch = useContext(DispatchContext);

  return [
    state,
    {
      previous: (callback, triggerType) =>
        dispatch({
          type: 'prev',
          callback,
          triggerType,
        }),
      next: (callback, triggerType) =>
        dispatch({
          type: 'next',
          callback,
          triggerType,
        }),
      current: (indx, callback, triggerType) =>
        dispatch({
          type: 'current',
          data: indx,
          callback,
          triggerType,
        }),
    },
  ];
};

interface CarouselProviderProps {
  initialState: State;
  children: React.ReactElement | React.ReactElement[];
}

export const CarouselProvider = ({
  initialState,
  children,
}: CarouselProviderProps) => {
  const firstUpdate = useRef(true);
  const [state, dispatch] = useReducer(reducer, initialState);

  useIsomorphicEffect(() => {
    if (!firstUpdate.current) {
      dispatch({ type: 'reset', data: initialState.totalItems });
    } else {
      firstUpdate.current = false;
    }
  }, [initialState.totalItems]);

  return (
    <Context.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </Context.Provider>
  );
};
